<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex items-center gap-4">
                                <button type="button" class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500" @click="$router.push(`/template/${templateId}`)">
                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                        </svg>
                                </button>
                                <div class="flex-1">
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Template State</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">State ID: {{ stateId }}</p>
                                </div>
                                <div v-if="!isLoading">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="saveState"
                                                :disabled="isSaving"
                                        >
                                                {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                        </button>
                                </div>
                        </div>
                </header>

                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading template state...</span>
                </div>

                <main v-else class="p-6">
                        <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
                                        <h2 class="text-lg font-semibold text-gray-900 m-0">State Properties</h2>
                                </div>
                                <div class="p-6">
                                        <div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 mb-6">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                                        <select v-model="state.type" class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-5 focus:outline-none transition-colors">
                                                                <option value="photo">photo</option>
                                                                <option value="text">text</option>
                                                        </select>
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                                                        <input
                                                                v-model.number="state.priority"
                                                                type="number"
                                                                class="w-full py-2 px-3 border border-gray-300 rounded-lg text-sm box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-5 focus:outline-none transition-colors"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-8">
                                                <label class="flex items-center gap-2 cursor-pointer">
                                                        <div class="relative">
                                                                <input type="checkbox" :checked="!!state.combine" @change="state.combine = ($event.target as HTMLInputElement).checked ? 1 : 0" class="sr-only peer">
                                                                <div class="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-orange-7 transition-colors"></div>
                                                                <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-5 transition-transform"></div>
                                                        </div>
                                                        <span class="text-sm font-medium text-gray-700">Combine</span>
                                                </label>
                                        </div>
                                </div>

                                <div class="flex items-center justify-between gap-4 py-4 px-6 border-t border-gray-200 border-b border-gray-200">
                                        <h3 class="text-base font-semibold text-gray-900 m-0">Position Properties</h3>
                                        <div class="flex gap-2">
                                                <button
                                                        v-if="state.type === 'photo'"
                                                        type="button"
                                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        :disabled="isAddingPosition"
                                                        @click="addPosition('photo')"
                                                >
                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        {{ isAddingPosition ? 'Adding...' : 'Add Photo Position' }}
                                                </button>
                                                <button
                                                        v-if="state.type === 'text'"
                                                        type="button"
                                                        class="inline-flex items-center gap-2 py-2 px-4 bg-orange-7 hover:bg-orange-6 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        :disabled="isAddingPosition"
                                                        @click="addPosition('text')"
                                                >
                                                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                        </svg>
                                                        {{ isAddingPosition ? 'Adding...' : 'Add Text Position' }}
                                                </button>
                                        </div>
                                </div>
                                <div v-if="isLoadingPositions" class="flex justify-center items-center py-12 px-6">
                                        <svg class="w-8 h-8 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span class="ml-3 text-gray-500 text-sm">Loading positions...</span>
                                </div>
                                <div v-else-if="positions.length === 0" class="py-12 px-6 text-center text-gray-500">No positions found</div>
                                <div v-else class="p-6 flex flex-col gap-3">
                                        <div v-for="position in positions" :key="position.id + '-' + position.type" class="border border-gray-200 rounded-xl overflow-hidden">
                                                <div
                                                        class="flex items-center gap-3 py-3 px-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                                        @click="toggleExpand(position)"
                                                >
                                                        <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-90': isExpanded(position) }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                                                        </svg>
                                                        <span
                                                                class="inline-block text-xs font-semibold uppercase px-2 py-0.5 rounded"
                                                                :class="position.type === 'photo' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'"
                                                        >{{ position.type }}</span>
                                                        <span class="text-sm font-medium text-gray-900">ID: {{ position.id }}</span>
                                                        <span class="text-xs text-gray-400 ml-auto mr-2">{{ position.x_axis ?? '—' }}, {{ position.y_axis ?? '—' }} &middot; {{ position.width ?? '—' }}&times;{{ position.height ?? '—' }}</span>
                                                        <button
                                                                type="button"
                                                                class="flex items-center justify-center w-8 h-8 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500 hover:bg-gray-100 transition-colors"
                                                                @click.stop="editPosition(position)"
                                                                title="Edit"
                                                        >
                                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                                </svg>
                                                        </button>
                                                </div>
                                                <div v-if="isExpanded(position)" class="bg-gray-50 border-t border-gray-200 px-4 py-4">
                                                        <div class="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-x-6 gap-y-3">
                                                                <div v-for="prop in getPositionProperties(position)" :key="prop.label">
                                                                        <div class="text-xs font-medium text-gray-500 uppercase mb-0.5">{{ prop.label }}</div>
                                                                        <div class="text-sm text-gray-900">{{ prop.value ?? '—' }}</div>
                                                                </div>
                                                        </div>
                                                </div>
                                        </div>
                                </div>
                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter, isNavigationFailure } from 'vue-router';
import { TemplatePositionStateModel } from '@sosocio/creator/interfaces';
import { httpClient } from 'utils/http';
import Swal from 'sweetalert2';

interface PositionItem {
        id: number;
        type: string;
        [key: string]: any;
}

export default defineComponent({
        name: 'EditTemplateStatePage',
        setup() {
                const route = useRoute();
                const router = useRouter();
                const templateId = route.params.templateid as string;
                const stateId = route.params.id as string;

                const state = ref<Partial<TemplatePositionStateModel>>({});
                const positions = ref<PositionItem[]>([]);
                const expandedKeys = ref<Set<string>>(new Set());
                const isLoading = ref(false);
                const isLoadingPositions = ref(false);
                const isSaving = ref(false);
                const isAddingPosition = ref(false);

                const positionKey = (p: PositionItem) => `${p.type}-${p.id}`;
                const toggleExpand = (p: PositionItem) => {
                        const key = positionKey(p);
                        if (expandedKeys.value.has(key)) {
                                expandedKeys.value.delete(key);
                        } else {
                                expandedKeys.value.add(key);
                        }
                        expandedKeys.value = new Set(expandedKeys.value);
                };
                const isExpanded = (p: PositionItem) => expandedKeys.value.has(positionKey(p));

                const photoPropertyLabels: { key: string; label: string }[] = [
                        { key: 'x_axis', label: 'X' },
                        { key: 'y_axis', label: 'Y' },
                        { key: 'z_axis', label: 'Z' },
                        { key: 'width', label: 'Width' },
                        { key: 'height', label: 'Height' },
                        { key: 'angle', label: 'Angle' },
                        { key: 'borderimageid', label: 'Border Image ID' },
                        { key: 'borderwidth', label: 'Border Width' },
                        { key: 'bordercolor', label: 'Border Color' },
                        { key: 'autorotate', label: 'Auto Rotate' },
                        { key: 'ratio', label: 'Ratio' },
                        { key: 'transformable', label: 'Transformable' },
                        { key: 'mask', label: 'Mask' },
                        { key: 'fillMethod', label: 'Fill Method' },
                        { key: 'flag', label: 'Flag' },
                        { key: 'matrix', label: 'Matrix' },
                        { key: 'overlap_top', label: 'Overlap Top' },
                        { key: 'overlap_right', label: 'Overlap Right' },
                        { key: 'overlap_bottom', label: 'Overlap Bottom' },
                        { key: 'overlap_left', label: 'Overlap Left' },
                ];
                const textPropertyLabels: { key: string; label: string }[] = [
                        { key: 'x_axis', label: 'X' },
                        { key: 'y_axis', label: 'Y' },
                        { key: 'z_axis', label: 'Z' },
                        { key: 'width', label: 'Width' },
                        { key: 'height', label: 'Height' },
                        { key: 'angle', label: 'Angle' },
                        { key: 'fontface', label: 'Font Face' },
                        { key: 'pointsize', label: 'Point Size' },
                        { key: 'fontcolor', label: 'Font Color' },
                        { key: 'fontbold', label: 'Bold' },
                        { key: 'fontitalic', label: 'Italic' },
                        { key: 'align', label: 'Alignment' },
                        { key: 'borderwidth', label: 'Border Width' },
                        { key: 'bordercolor', label: 'Border Color' },
                        { key: 'bgcolor', label: 'Background Color' },
                        { key: 'transformable', label: 'Transformable' },
                        { key: 'styleable', label: 'Styleable' },
                        { key: 'snap', label: 'Snap' },
                        { key: 'overlay', label: 'Overlay' },
                        { key: 'productattribute', label: 'Product Attribute' },
                        { key: 'overlap_top', label: 'Overlap Top' },
                        { key: 'overlap_right', label: 'Overlap Right' },
                        { key: 'overlap_bottom', label: 'Overlap Bottom' },
                        { key: 'overlap_left', label: 'Overlap Left' },
                ];
                const getPositionProperties = (p: PositionItem) => {
                        const labels = p.type === 'photo' ? photoPropertyLabels : textPropertyLabels;
                        return labels.map(({ key, label }) => ({ label, value: p[key] }));
                };

                const loadState = async () => {
                        isLoading.value = true;
                        try {
                                const { data } = await httpClient.get<TemplatePositionStateModel>(`/api/templatestate/${stateId}`);
                                state.value = data;
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to load template state',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadPositions = async (rethrow = false) => {
                        isLoadingPositions.value = true;
                        const stateIdNum = Number(stateId);
                        const fetchPositions = async (endpoint: string, type: string): Promise<PositionItem[]> => {
                                try {
                                        const params = new URLSearchParams({
                                                limit: '0',
                                                where: JSON.stringify({ templatestateid: stateIdNum }),
                                        });
                                        const { data } = await httpClient.get<any[]>(`/api/${endpoint}?${params}`);
                                        return data.map((p: any) => ({ ...p, type }));
                                } catch (error: any) {
                                        console.error(`Failed to load ${type} positions:`, error);
                                        if (rethrow) throw error;
                                        return [];
                                }
                        };
                        try {
                                const [photoPositions, textPositions] = await Promise.all([
                                        fetchPositions('templatepositionphoto', 'photo'),
                                        fetchPositions('templatepositiontext', 'text'),
                                ]);
                                positions.value = [...photoPositions, ...textPositions];
                        } finally {
                                isLoadingPositions.value = false;
                        }
                };

                const saveState = async () => {
                        isSaving.value = true;
                        try {
                                await httpClient.put(`/api/templatestate/${stateId}`, state.value);
                                Swal.fire({
                                        title: 'Saved',
                                        text: 'Template state has been saved',
                                        icon: 'success',
                                        timer: 1500,
                                });
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to save template state',
                                        icon: 'error',
                                });
                        } finally {
                                isSaving.value = false;
                        }
                };

                const addPosition = async (type: 'photo' | 'text') => {
                        isAddingPosition.value = true;
                        try {
                                const endpoint = type === 'photo' ? 'templatepositionphoto' : 'templatepositiontext';
                                const { data } = await httpClient.post<any>(`/api/${endpoint}`, {
                                        templatestateid: Number(stateId),
                                });
                                const newId = data?.id;
                                if (newId === undefined || newId === null) {
                                        throw new Error('Position creation did not return an id');
                                }
                                let refreshFailed = false;
                                try {
                                        // Rethrow so a failed reload is observable and the
                                        // existing list is preserved instead of being replaced.
                                        await loadPositions(true);
                                } catch {
                                        refreshFailed = true;
                                }
                                let navigationFailed = false;
                                try {
                                        // Vue Router 4 resolves aborted/cancelled navigations
                                        // with a NavigationFailure instead of rejecting.
                                        const navResult = await router.push({
                                                path: `/template/${templateId}/state/${stateId}/position/${newId}`,
                                                query: { type },
                                        });
                                        if (isNavigationFailure(navResult)) {
                                                navigationFailed = true;
                                        }
                                } catch {
                                        navigationFailed = true;
                                }
                                if (navigationFailed) {
                                        Swal.fire({
                                                title: 'Position created',
                                                text: refreshFailed
                                                        ? `The ${type} position (ID ${newId}) was created, but the list could not be refreshed and its edit screen could not be opened. Reload the page to see it.`
                                                        : `The ${type} position (ID ${newId}) was created, but its edit screen could not be opened. Use the Edit button in the list to open it.`,
                                                icon: 'warning',
                                        });
                                }
                        } catch (error: any) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || `Failed to create ${type} position`,
                                        icon: 'error',
                                });
                        } finally {
                                isAddingPosition.value = false;
                        }
                };

                const editPosition = (position: PositionItem) => {
                        router.push({
                                path: `/template/${templateId}/state/${stateId}/position/${position.id}`,
                                query: { type: position.type },
                        });
                };

                onMounted(async () => {
                        await Promise.all([loadState(), loadPositions()]);
                });

                return {
                        templateId,
                        stateId,
                        state,
                        positions,
                        isLoading,
                        isLoadingPositions,
                        isSaving,
                        saveState,
                        addPosition,
                        isAddingPosition,
                        editPosition,
                        toggleExpand,
                        isExpanded,
                        getPositionProperties,
                };
        },
});
</script>
